import { useMutation, useQueryClient } from '@tanstack/react-query';
import { keys, branchKey } from '@/lib/queryKeys';
import { createPharmacyBill } from './api';
import type { CreatePharmacyBillInput } from './types';

/**
 * Invalidates inventory (dispensing reduces quantity_available) and the
 * active-prescriptions list (a fully-dispensed prescription auto-completes
 * server-side — see billing.js's POST /pharmacy/create — and should drop off
 * the Dispensing screen's list on the next read) plus that one prescription's
 * own detail (its per-item dispensed/remaining quantity just changed).
 *
 * refetchType: 'all' on every call here, not the invalidateQueries default of
 * 'active' — DispensingTab.tsx's own onSuccess (passed to .mutate(), which
 * TanStack Query runs AFTER this hook-level onSuccess) clears selConsult
 * right after this fires, which disables usePrescriptionDetail's query. With
 * the default 'active' refetchType, whether that query still counts as
 * "active" at the instant invalidateQueries processes it is a timing race
 * against React's render/effect scheduling — lose it, and the query is only
 * marked stale rather than actually refetched, so the badge kept showing
 * pre-dispense dispensed/remaining counts until enough time passed for
 * staleTime to lapse on its own. 'all' refetches unconditionally regardless
 * of whether an observer is currently subscribed, so the cache is correct by
 * the time the prescription is reselected no matter how that race lands.
 */
export function useCreatePharmacyBill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePharmacyBillInput) => createPharmacyBill(input),
    onSuccess: (_bill, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.pharmacy.inventory(), refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: keys.prescriptions.list({ status: 'active' }), refetchType: 'all' });
      // The mutation only knows prescription_item_id, not which prescription(s)
      // it belongs to, so invalidate every open prescription detail query by
      // the shared branchKey('prescription', …) prefix rather than looking up
      // the id(s) just to build an exact key — a false-positive refetch of an
      // unrelated prescription detail is harmless; a stale dispensed/remaining
      // count on the one that WAS touched is not.
      if (variables.items.some((i) => i.prescription_item_id)) {
        queryClient.invalidateQueries({ queryKey: branchKey('prescription'), refetchType: 'all' });
      }
    },
  });
}
